import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ZapIcon, CornerDownRightIcon, ArrowDownIcon } from 'lucide-react';
const triggers = [{
  event: 'Policy bound',
  action: 'Send subjectivities request'
}, {
  event: 'Loss control recs issued',
  action: 'Send corrective action follow-ups'
}, {
  event: 'Policy expired',
  action: 'Auto-send pre-filled premium audit'
}, {
  event: 'Renewal due · 60 days',
  action: 'Follow up with producer'
}];
export function TriggersMock() {
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setActiveIdx(i => (i + 1) % triggers.length);
    }, 2400);
    return () => clearInterval(id);
  }, []);
  const active = triggers[activeIdx];
  return <div className="relative" data-id="element-406">
      <div aria-hidden className="pointer-events-none absolute -inset-x-6 -inset-y-4 rounded-[2rem] bg-accent/5 blur-3xl" data-id="element-407" />
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/80 shadow-2xl shadow-black/40 backdrop-blur" data-id="element-408">
        {/* Header bar */}
        <div className="flex items-center border-b border-white/5 px-5 py-3.5" data-id="element-409">
          <div className="flex items-center gap-2" data-id="element-410">
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-accent/10 ring-1 ring-accent/20" data-id="element-411">
              <ZapIcon className="h-3 w-3 text-accent" data-id="element-412" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400" data-id="element-413">
              Automated triggers
            </span>
          </div>
        </div>

        {/* MOBILE: single morphing card — event flows down into action */}
        <div className="block px-5 py-6 lg:hidden" data-id="element-414">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500" data-id="element-415">
            When this happens
          </div>
          <div className="relative mt-2 min-h-[3.25rem]" data-id="element-416">
            <AnimatePresence mode="wait" data-id="element-417">
              <motion.div key={`evt-${activeIdx}`} initial={{
              opacity: 0,
              y: 8,
              filter: 'blur(4px)'
            }} animate={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} exit={{
              opacity: 0,
              y: -8,
              filter: 'blur(4px)'
            }} transition={{
              duration: 0.5,
              ease: [0.16, 1, 0.3, 1]
            }} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3" data-id="element-418">
                <span className="relative flex h-2 w-2 flex-shrink-0 items-center justify-center rounded-full bg-accent" data-id="element-419">
                  <motion.span className="absolute inset-0 rounded-full bg-accent" animate={{
                  scale: [1, 2.4],
                  opacity: [0.6, 0]
                }} transition={{
                  duration: 1.4,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} data-id="element-420" />
                </span>
                <span className="text-[15px] leading-tight text-white" data-id="element-421">
                  {active.event}
                </span>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Connector — animated downward flow */}
          <div className="my-3 flex justify-center" data-id="element-422">
            <motion.div key={`flow-${activeIdx}`} initial={{
            scaleY: 0,
            opacity: 0
          }} animate={{
            scaleY: 1,
            opacity: 1
          }} transition={{
            duration: 0.45,
            ease: 'easeOut',
            delay: 0.15
          }} className="origin-top" data-id="element-423">
              <div className="flex flex-col items-center gap-1" data-id="element-424">
                <div className="h-6 w-px bg-gradient-to-b from-transparent via-accent/60 to-accent" data-id="element-425" />
                <div className="flex h-5 w-5 items-center justify-center rounded-full border border-accent/40 bg-accent/15" data-id="element-426">
                  <ArrowDownIcon className="h-2.5 w-2.5 text-accent" data-id="element-427" />
                </div>
              </div>
            </motion.div>
          </div>

          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent" data-id="element-428">
            Pigeon runs
          </div>
          <div className="relative mt-2 min-h-[4rem]" data-id="element-429">
            <AnimatePresence mode="wait" data-id="element-430">
              <motion.div key={`act-${activeIdx}`} initial={{
              opacity: 0,
              y: 8,
              filter: 'blur(4px)'
            }} animate={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} exit={{
              opacity: 0,
              y: -8,
              filter: 'blur(4px)'
            }} transition={{
              duration: 0.5,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.1
            }} className="rounded-xl border border-accent/30 bg-gradient-to-br from-accent/[0.10] to-accent/[0.02] p-4" data-id="element-431">
                <div className="text-[15px] font-semibold leading-snug tracking-tight text-white" data-id="element-432">
                  {active.action}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Step indicator */}
          <div className="mt-4 flex justify-center gap-1.5" data-id="element-433">
            {triggers.map((_, i) => <span key={i} className={`h-1 rounded-full transition-all duration-500 ${i === activeIdx ? 'w-6 bg-accent' : 'w-1 bg-white/15'}`} data-id="element-434" />)}
          </div>
        </div>

        {/* DESKTOP: two-column listening / running */}
        <div className="hidden gap-0 lg:grid lg:grid-cols-[1fr_1fr]" data-id="element-435">
          {/* Events list */}
          <div className="border-b border-white/5 p-5 lg:border-b-0 lg:border-r" data-id="element-436">
            <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500" data-id="element-437">
              Listening for
            </div>
            <ul className="space-y-2" data-id="element-438">
              {triggers.map((t, i) => {
              const isActive = i === activeIdx;
              return <li key={t.event} className={`relative flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors duration-500 ${isActive ? 'border-accent/40 bg-accent/[0.06]' : 'border-white/5 bg-white/[0.02]'}`} data-id="element-439">
                    <span className={`relative flex h-2 w-2 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-500 ${isActive ? 'bg-accent' : 'bg-zinc-600'}`} data-id="element-440">
                      {isActive && <motion.span className="absolute inset-0 rounded-full bg-accent" animate={{
                    scale: [1, 2.4],
                    opacity: [0.6, 0]
                  }} transition={{
                    duration: 1.4,
                    repeat: Infinity,
                    ease: 'easeOut'
                  }} data-id="element-441" />}
                    </span>
                    <span className={`flex-1 truncate text-sm transition-colors duration-500 ${isActive ? 'text-white' : 'text-zinc-400'}`} data-id="element-442">
                      {t.event}
                    </span>
                  </li>;
            })}
            </ul>
          </div>

          {/* Active workflow */}
          <div className="relative p-5" data-id="element-443">
            <div className="mb-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500" data-id="element-444">
              <CornerDownRightIcon className="h-3 w-3 flex-shrink-0 text-accent" data-id="element-445" />
              <span data-id="element-446">Pigeon runs</span>
            </div>

            <div className="relative min-h-[7rem] overflow-visible" data-id="element-447">
              <AnimatePresence mode="wait" data-id="element-448">
                <motion.div key={activeIdx} initial={{
                opacity: 0,
                y: 14,
                filter: 'blur(6px)'
              }} animate={{
                opacity: 1,
                y: 0,
                filter: 'blur(0px)'
              }} exit={{
                opacity: 0,
                y: -10,
                filter: 'blur(6px)'
              }} transition={{
                duration: 0.55,
                ease: [0.16, 1, 0.3, 1]
              }} className="absolute inset-x-0 top-0" data-id="element-449">
                  <div className="rounded-xl border border-accent/30 bg-gradient-to-br from-accent/[0.08] to-accent/[0.02] p-4" data-id="element-450">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent" data-id="element-451">
                      Triggered
                    </div>
                    <div className="mt-2 text-base font-semibold leading-snug tracking-tight text-white" data-id="element-452">
                      {active.action}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>;
}